import { combineLatest, Observable, of } from 'rxjs'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'

import { Component, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'

import { subscriptionActions } from '../../subscription/subscription.actions'
import { subscriptionSelectors } from '../../subscription/subscription.selectors'
import { Subscription } from '../../subscription/subscription'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { BulkAction, BulkActionEvent } from 'src/app/shared/interfaces'
import { NotificationService } from 'src/app/core/notification.service'
import { Organization } from '../../organization/organization'
import { organizationActions } from '../../organization/organization.actions'
import { organizationSelectors } from '../../organization/organization.selectors'
import { OrganizationService } from '../organization.service'
import { ActivatedRoute } from '@angular/router'

@Component({
  selector: 'organization-required-subscription',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class OrganizationRequiredSubscriptionTabComponent implements OnInit {
  public organization$: Observable<Organization>
  public organizationId$: Observable<number>

  public attachedSubscriptions$: Observable<Subscription[]>
  public allSubscriptions$: Observable<Subscription[]>
  public availableSubscriptions$: Observable<Subscription[]>

  public BulkAction = BulkAction

  public isLoading$: Observable<boolean>

  constructor(
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private organizationService: OrganizationService,
    private activeRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.isLoading$ = this.store.select(subscriptionSelectors.selectIsLoading)

    if (this.organizationService.showModal) {
      this.activeRoute.params.pipe(take(1)).subscribe((params) => {
        if (params.hasOwnProperty('organizationId')) {
          this.organizationId$ = of(Number(params['organizationId']))
        }
      })
    } else {
      this.organizationId$ = this.store
        .select(organizationSelectors.selectIdRouteParam)
        .pipe(filter((id) => Boolean(id)))
    }

    this.organization$ = this.organizationId$.pipe(
      tap((id) =>
        this.store.dispatch(
          organizationActions.fetchOneById({
            id,
            params: RequestQueryBuilder.create({
              join: [{ field: 'requiredSubscriptions' }]
            })
          })
        )
      ),
      switchMap((organizationId) =>
        this.store.select(organizationSelectors.selectById, organizationId)
      )
    )

    this.store.dispatch(
      subscriptionActions.fetchMany({
        params: RequestQueryBuilder.create({
          join: [
            { field: 'tags' },
            { field: 'location' },
            { field: 'translations' },
            { field: 'prerequisiteSubscriptions' }
          ]
        })
      })
    )

    this.allSubscriptions$ = this.store
      .select(subscriptionSelectors.selectAll)
      .pipe(
        map((subscriptions) =>
          subscriptions.filter((subscription) => subscription.isActive)
        )
      )

    this.attachedSubscriptions$ = combineLatest([
      this.organization$,
      this.allSubscriptions$
    ]).pipe(
      map(([organization, allSubscriptions]) => {
        return allSubscriptions.filter((item) =>
          organization?.requiredSubscriptions?.includes(item.id)
        )
      })
    )

    this.availableSubscriptions$ = combineLatest([
      this.organization$,
      this.allSubscriptions$
    ]).pipe(
      map(([organization, allSubscriptions]) => {
        return allSubscriptions.filter(
          (item) => !organization?.requiredSubscriptions?.includes(item.id)
        )
      })
    )
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent) {
    this.organization$.pipe(take(1)).subscribe((organization) => {
      switch (bulkActionEvent.action) {
        case BulkAction.DETACH:
          bulkActionEvent.items.map((item) =>
            this.store.dispatch(
              organizationActions.deleteOneRequiredSubscriptionLink({
                organizationId: organization.id,
                subscriptionId: item.id
              })
            )
          )
          break
        case BulkAction.ATTACH:
          bulkActionEvent.items.map((item) =>
            this.store.dispatch(
              organizationActions.createOneRequiredSubscriptionLink({
                organizationId: organization.id,
                subscriptionId: item.id
              })
            )
          )
          break
      }

      this.notificationService.emit('alerts.success', 'success')
    })
  }
}
